/**
 * DevOverrideContext
 * ------------------
 * Localhost-only developer tool. When `bypassEntitlements` is true,
 * `useEntitlement()` returns a synthetic max-privilege result so every
 * entitlement gate opens. Flip it off to test the real user/role/plan.
 *
 * Only ever active on localhost — in any hosted environment the bypass
 * flag has no effect regardless of its stored value.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import type { ReactNode } from 'react';

const STORAGE_KEY = 'jamcam-dev-bypass-entitlements';

function isLocalhostEnv(): boolean {
  if (typeof window === 'undefined') return false;
  const h = window.location.hostname;
  return (
    h === 'localhost' ||
    h === '127.0.0.1' ||
    h === '::1' ||
    h.endsWith('.local')
  );
}

export interface DevOverrideContextValue {
  /** True only when running on localhost / 127.0.0.1 / *.local */
  isLocalhost: boolean;
  developerMode: boolean;
  setDeveloperMode: (value: boolean) => void;
  /**
   * When true (default on localhost), `useEntitlement()` returns a
   * synthetic admin + paid result, unlocking every feature gate.
   * Ignored entirely in non-localhost environments.
   */
  bypassEntitlements: boolean;
  setBypassEntitlements: (v: boolean) => void;
}

const DevOverrideContext = createContext<DevOverrideContextValue>({
  isLocalhost: false,
  developerMode: false,
  setDeveloperMode: () => undefined,
  bypassEntitlements: false,
  setBypassEntitlements: () => undefined,
});

export function DevOverrideProvider({ children }: { children: ReactNode }) {
  const isLocalhost = useMemo(() => isLocalhostEnv(), []);

  const [developerMode, setDeveloperModeState] = useState(() => {
    try {
      return (
        isLocalhost && localStorage.getItem('jamcam-developer-mode') === 'true'
      );
    } catch {
      return false;
    }
  });
  const setDeveloperMode = useCallback(
    (value: boolean) => {
      setDeveloperModeState(isLocalhost && value);
      try {
        localStorage.setItem(
          'jamcam-developer-mode',
          String(isLocalhost && value)
        );
      } catch {
        /* optional persistence */
      }
    },
    [isLocalhost]
  );
  const [bypassEntitlements, setBypassEntitlementsState] = useState<boolean>(
    () => {
      if (!isLocalhostEnv()) return false;
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        // Default to `true` on first visit on localhost
        return stored === null ? true : stored === 'true';
      } catch {
        return true;
      }
    }
  );

  const setBypassEntitlements = useCallback((v: boolean) => {
    setBypassEntitlementsState(v);
    try {
      localStorage.setItem(STORAGE_KEY, String(v));
    } catch {
      // Ignore blocked storage.
    }
  }, []);

  const value = useMemo<DevOverrideContextValue>(
    () => ({
      isLocalhost,
      developerMode,
      setDeveloperMode,
      bypassEntitlements,
      setBypassEntitlements,
    }),
    [
      isLocalhost,
      developerMode,
      setDeveloperMode,
      bypassEntitlements,
      setBypassEntitlements,
    ]
  );

  return (
    <DevOverrideContext.Provider value={value}>
      {children}
    </DevOverrideContext.Provider>
  );
}

export function useDevOverride(): DevOverrideContextValue {
  return useContext(DevOverrideContext);
}

/**
 * Played-note detection (microphone + hand tracking) is a localhost-only
 * beta shown in developer mode. The tuner is not part of it.
 */
export function useNoteTrackingBeta(): boolean {
  return useContext(DevOverrideContext).developerMode;
}
